import { useEffect, useRef, useState } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  ClipboardCopy,
  Clock3,
  HelpCircle,
} from 'lucide-react'
import { steps } from '../content/steps'
import { renderStepText } from '../lib/step-content'
import { useActivity } from '../state/activity-context'

export function StepView() {
  const {
    state,
    updateField,
    updateInstructionProgress,
    goToStep,
  } = useActivity()
  const [copyMessage, setCopyMessage] = useState('')
  const titleRef = useRef<HTMLHeadingElement>(null)
  const previousStepIndex = useRef(state.currentStepIndex)
  const step = steps[state.currentStepIndex]
  const isFirst = state.currentStepIndex === 0
  const isLast = state.currentStepIndex === steps.length - 1
  const isTask = step.kind === 'task'
  const activeInstructionIndex = Math.min(
    state.instructionProgressByStep[step.id] ?? 0,
    step.instructions.length - 1,
  )
  const isLastInstruction =
    activeInstructionIndex === step.instructions.length - 1
  const progress = ((state.currentStepIndex + 1) / steps.length) * 100
  const renderText = (text: string) =>
    renderStepText(text, state.fieldValues)
  const hasRequiredName =
    !isFirst || Boolean(state.fieldValues.student_name?.trim())
  const canAdvance = hasRequiredName

  useEffect(() => {
    if (previousStepIndex.current !== state.currentStepIndex) {
      window.scrollTo({ top: 0, behavior: 'instant' })
      titleRef.current?.focus()
      previousStepIndex.current = state.currentStepIndex
    }
  }, [state.currentStepIndex])

  async function copyValue(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(renderText(value))
      setCopyMessage(`${label} copiado.`)
    } catch {
      setCopyMessage(
        'Não foi possível copiar. Selecione o texto ou chame o professor.',
      )
    }
    window.setTimeout(() => setCopyMessage(''), 2400)
  }

  return (
    <main
      className={`guide-stage guide-stage-${step.kind}`}
      id="main-content"
    >
      <div className="slide-progress">
        <div className="slide-progress-copy">
          <span>{step.block}</span>
          <strong>
            Tela {state.currentStepIndex + 1} de {steps.length}
          </strong>
        </div>
        <div
          className="slide-progress-track"
          role="progressbar"
          aria-label="Progresso nas telas da atividade"
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-valuenow={state.currentStepIndex + 1}
        >
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>

      <article className="guide-slide">
        <header className="slide-heading">
          <div>
            <span className="slide-phase">{step.phase}</span>
            <h1 ref={titleRef} tabIndex={-1}>
              {renderText(step.title)}
            </h1>
          </div>
          {step.minutes && (
            <span className="time-chip">
              <Clock3 size={18} aria-hidden="true" />
              Até {step.minutes} min
            </span>
          )}
        </header>

        <section
          className="context-card"
          aria-labelledby="contextualizacao-title"
        >
          <span id="contextualizacao-title">Contexto</span>
          <p>{renderText(step.objective)}</p>
        </section>

        <section className="request-card" aria-labelledby="pedido-title">
          <span id="pedido-title">Pedido guiado</span>
          <p>{renderText(step.action)}</p>
        </section>

        {step.warning && (
          <aside className="guide-warning" role="note">
            <strong>Atenção:</strong> {renderText(step.warning)}
          </aside>
        )}

        {step.fields?.length ? (
          <div className="guide-fields">
            {step.fields.map((field) => (
              <label key={field.key}>
                <span>{field.label}</span>
                <input
                  type="text"
                  value={state.fieldValues[field.key] ?? ''}
                  onChange={(event) =>
                    updateField(step.id, field.key, event.target.value)
                  }
                  placeholder={field.placeholder}
                  autoComplete="off"
                  data-testid={`field-${field.key}`}
                />
                {field.help && <small>{field.help}</small>}
              </label>
            ))}
          </div>
        ) : null}

        {isFirst && (
          <div className="start-inline">
            <span role="status">
              {hasRequiredName
                ? 'Nome salvo. Você pode começar.'
                : 'Digite seu nome para começar.'}
            </span>
            <button
              type="button"
              className="slide-button slide-button-primary"
              disabled={!canAdvance}
              onClick={() => goToStep(state.currentStepIndex + 1)}
              data-testid="next-step"
            >
              Começar atividade
              <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
        )}

        <div className="slide-work-grid">
          <section className="steps-card" aria-labelledby="steps-title">
            <div className="steps-heading">
              <span aria-hidden="true">
                {isTask
                  ? `${activeInstructionIndex + 1}/${step.instructions.length}`
                  : String(step.instructions.length).padStart(2, '0')}
              </span>
              <div>
                <small>
                  {isTask ? 'Uma ação por vez' : 'Faça nesta ordem'}
                </small>
                <h2 id="steps-title">PASSOS</h2>
              </div>
            </div>
            <ol
              className={isTask ? 'single-active-step' : undefined}
              start={isTask ? activeInstructionIndex + 1 : undefined}
              aria-live={isTask ? 'polite' : undefined}
            >
              {(isTask
                ? [step.instructions[activeInstructionIndex]]
                : step.instructions
              ).map((instruction, index) => {
                const visibleIndex = isTask
                  ? activeInstructionIndex
                  : index
                return (
                  <li key={`${visibleIndex}-${instruction}`}>
                    <span>{visibleIndex + 1}</span>
                    <p>{renderText(instruction)}</p>
                  </li>
                )
              })}
            </ol>
            {isTask && (
              <p className="instruction-return-hint">
                {isLastInstruction
                  ? 'Faça esta ação, confira o resultado esperado e escolha “Próxima tela”.'
                  : 'Faça esta ação no programa. Quando terminar, volte ao site e escolha “Ver próximo passo”.'}
              </p>
            )}
            {isTask && (
              <div className="instruction-navigation">
                <button
                  type="button"
                  disabled={activeInstructionIndex === 0}
                  onClick={() =>
                    updateInstructionProgress(
                      step.id,
                      activeInstructionIndex - 1,
                    )
                  }
                >
                  <ArrowLeft size={18} aria-hidden="true" />
                  Passo anterior
                </button>
                {!isLastInstruction && (
                  <button
                    type="button"
                    onClick={() =>
                      updateInstructionProgress(
                        step.id,
                        activeInstructionIndex + 1,
                      )
                    }
                    data-testid="next-instruction"
                  >
                    Ver próximo passo
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                )}
                {isLastInstruction && !isLast && (
                  <button
                    type="button"
                    onClick={() => goToStep(state.currentStepIndex + 1)}
                    data-testid="next-step"
                  >
                    Próxima tela
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
          </section>

          <aside
            className="result-card"
            aria-labelledby="resultado-title"
          >
            <span id="resultado-title">Resultado esperado</span>
            <div className="result-preview">
              {step.expected.map((line) => (
                <p key={line}>{renderText(line)}</p>
              ))}
            </div>
          </aside>
        </div>

        {step.copyables?.length ? (
          <details className="copy-details">
            <summary>Abra o modelo ou os comandos para copiar</summary>
            <section
              className="copy-zone"
              aria-label="Textos e comandos para copiar"
            >
              {step.copyables.map((copyable) => (
                <article key={copyable.label}>
                  <div>
                    <strong>{copyable.label}</strong>
                    <pre tabIndex={0}>{renderText(copyable.value)}</pre>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      void copyValue(copyable.label, copyable.value)
                    }
                    aria-label={`Copiar ${copyable.label}`}
                  >
                    <ClipboardCopy size={17} aria-hidden="true" />
                    Copiar
                  </button>
                </article>
              ))}
            </section>
          </details>
        ) : null}

        {step.support && (
          <details className="single-help">
            <summary>
              <HelpCircle size={18} aria-hidden="true" />
              Preciso de ajuda
            </summary>
            <p>{renderText(step.support.notFound)}</p>
            <p>
              <strong>Se o resultado ficou diferente:</strong>{' '}
              {renderText(step.support.differentResult)}
            </p>
            <p>
              <strong>Chame o professor se:</strong>{' '}
              {renderText(step.support.askTeacherWhen)}
            </p>
          </details>
        )}
      </article>

      {!isFirst && (
        <nav className="slide-navigation" aria-label="Navegação do roteiro">
          <button
            type="button"
            className="slide-button slide-button-secondary"
            onClick={() => goToStep(state.currentStepIndex - 1)}
          >
            <ArrowLeft size={20} aria-hidden="true" />
            Voltar
          </button>

          <span>Seu avanço é salvo automaticamente neste computador.</span>

          {!isLast && !isTask && (
            <button
              type="button"
              className="slide-button slide-button-primary"
              disabled={!canAdvance}
              onClick={() => goToStep(state.currentStepIndex + 1)}
              data-testid="next-step"
            >
              Próxima tela
              <ArrowRight size={20} aria-hidden="true" />
            </button>
          )}
        </nav>
      )}
      {copyMessage && (
        <div className="toast" role="status">
          {copyMessage}
        </div>
      )}
    </main>
  )
}
